import { useCallback, useEffect, useMemo, useRef, useState, type BaseSyntheticEvent } from 'react';
import { useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { topUpSchema, type TopUpValues } from '../schemas/topUp.schema';
import { recordTopUp } from '../services/topUps';
import type { RecordedTopUp } from '../types/topUps.types';
import { isJustificationRequired } from '../utils/accessRefusal';
import { topUpSubmitErrors } from '../utils/topUpErrors';
import { CHOOSE_DEALER_FIRST_REASON } from '../utils/overBalanceCopy';
import { formatMinor } from '../utils/topUpAmount';
import type { TakingDealerLock } from './useTakingDealerLock';

export interface TopUpFormState {
    control: Control<TopUpValues>;
    isSubmitting: boolean;
    minimumMinor: number;
    recordedAmount: string | null;
    formError: string | null;
    blockedReason: string | null;
    reset: () => void;
    submit: (event?: BaseSyntheticEvent) => Promise<void>;
}

export interface UseTopUpFormOptions {
    agreementId: string;
    minimumMinor: number;
    takingDealerId: string | null;
    onRecorded?: (recordedAmount: string) => void;
    dealerSession?: Pick<TakingDealerLock, 'beginTopUp' | 'settle'>;
    /** A request begun before this form mounted is still in flight. */
    topUpPending?: boolean;
    onAccessRefused?: () => void;
    heldRecordedAmount?: string | null;
}

export function useTopUpForm({
    agreementId,
    minimumMinor,
    takingDealerId,
    onRecorded,
    dealerSession,
    topUpPending = false,
    onAccessRefused,
    heldRecordedAmount = null,
}: UseTopUpFormOptions): TopUpFormState {
    const blockedReason = takingDealerId === null ? CHOOSE_DEALER_FIRST_REASON : null;
    const resolver = useMemo(() => zodResolver(topUpSchema(minimumMinor)), [minimumMinor]);
    const [recordedAmount, setRecordedAmount] = useState<string | null>(null);
    const draftGeneration = useRef(0);
    const latestMinimumMinor = useRef(minimumMinor);
    const {
        control,
        handleSubmit,
        setValue,
        setError,
        reset: resetForm,
        formState: { dirtyFields, isSubmitting, errors },
    } = useForm<TopUpValues>({
        resolver,
        defaultValues: { amountGross: formatMinor(minimumMinor), paymentReference: '' },
    });

    useEffect(() => {
        if (dirtyFields.amountGross) return;
        setValue('amountGross', formatMinor(minimumMinor));
    }, [minimumMinor, dirtyFields.amountGross, setValue]);

    useEffect(() => {
        latestMinimumMinor.current = minimumMinor;
    }, [minimumMinor]);

    const emptyValues = useCallback(
        () => ({ amountGross: formatMinor(latestMinimumMinor.current), paymentReference: '' }),
        [],
    );

    const reset = useCallback(() => {
        draftGeneration.current += 1;
        resetForm(emptyValues());
        setRecordedAmount(null);
    }, [resetForm, emptyValues]);

    const submit = handleSubmit(async (values) => {
        if (takingDealerId === null) return;
        const submittedDealerId = takingDealerId;
        const submittedDraft = draftGeneration.current;
        const token = dealerSession?.beginTopUp(submittedDealerId);
        const isStale = () => submittedDraft !== draftGeneration.current;
        setRecordedAmount(null);
        let recorded: RecordedTopUp;
        try {
            recorded = await recordTopUp(agreementId, { ...values, takingDealerId: submittedDealerId });
        } catch (error) {
            // This call bypasses React Query, so the access refusal watcher cannot see it.
            if (isJustificationRequired(error, agreementId)) onAccessRefused?.();
            if (token) dealerSession?.settle(token, null);
            if (isStale()) return;
            const { fieldErrors, formError } = topUpSubmitErrors(error);
            for (const [field, message] of Object.entries(fieldErrors)) {
                setError(field as keyof TopUpValues, { message });
            }
            if (formError) setError('root.server', { message: formError });
            return;
        }
        onRecorded?.(recorded.amount);
        if (token) dealerSession?.settle(token, recorded.takingDealerId ?? submittedDealerId);
        if (isStale()) return;
        setRecordedAmount(recorded.amount);
        resetForm(emptyValues());
    });

    return {
        control,
        isSubmitting: isSubmitting || topUpPending,
        minimumMinor,
        recordedAmount: recordedAmount ?? heldRecordedAmount,
        formError: errors.root?.server?.message ?? null,
        blockedReason,
        reset,
        submit,
    };
}
